import React, { useRef, useEffect, useState } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Button, Circle, IconButton,
  useToast, useDisclosure, AlertDialog, AlertDialogOverlay,
  AlertDialogContent, AlertDialogHeader, AlertDialogBody,
  AlertDialogFooter, Spinner
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useAuth } from "../auth/authProvider";

import { handleCancel, handlePortal, handlePayment } from './subscriptionAction';
import { plans, gradientText, StarField } from './pricingUI';

export default function UpgradeModel({ onClose, userId, email }) {
  const { subscription } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const toast = useToast();
  const { isOpen: isDialogOpen, onOpen: onDialogOpen, onClose: onDialogClose } = useDisclosure();
  const cancelRef = useRef();
  const displayPlans = plans;

  const cancelPending = subscription?.[0]?.cancel_at_period_end;
  const cancelAtUnix = subscription?.[0]?.cancel_at;
  const cancelDateString = cancelAtUnix ? new Date(cancelAtUnix * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric'}): '';

  const hasPaidSub = subscription?.length > 0;
  const currentPriceId = hasPaidSub ? subscription[0].items.data[0].price.id : null;
  const currentIndex = hasPaidSub ? displayPlans.findIndex(p => p.priceId === currentPriceId) : displayPlans.findIndex(p => p.title === 'Basic');

  const basicIdx = displayPlans.findIndex(p => p.title === 'Basic');
  const isBasicCurrent = currentIndex === basicIdx && !hasPaidSub; 
  const showPortalLink = hasPaidSub;

  const onCancel = () => handleCancel({ userId, onClose, toast, setLoadingCancel });
  const onPortal = () => handlePortal({ userId, setLoadingPortal });
  const onPayment = (priceId) => handlePayment({ priceId, userId, email, toast, setLoadingPlan });

  return (
    <Box pos="fixed" inset={0} bg="#0E0C15" zIndex="999" overflowY="auto">
      <IconButton aria-label="Close" icon={<CloseIcon />} pos="absolute" top={4} right={4}
        color="white" bg="transparent" _hover={{ bg: 'whiteAlpha.200' }} onClick={onClose} zIndex={10} />

      <StarField />

      <Box pt={16} textAlign="center">
        <HStack justify="center" spacing={2} mb={2}>
          <Text fontSize="12px" letterSpacing="15%" textTransform="uppercase" sx={gradientText}>[</Text>
          <Text fontFamily="Inter" fontWeight="400" fontSize="12px" letterSpacing="15%" textTransform="uppercase" color="#ADA8C3">
            GET STARTED WITH GRID AI TODAY
          </Text>
          <Text fontSize="12px" letterSpacing="15%" textTransform="uppercase" sx={gradientText}>]</Text>
        </HStack>
        <Text fontFamily="Inter" fontWeight="500" fontSize="48px" textAlign="center" color="white" mb={10}>
          Enhance Your AI Experience
        </Text>
      </Box>

      <Flex direction="column" align="center" justify="center" minH="80vh" p={6}>
        <HStack spacing={6} flexWrap="wrap" justify="center" align="flex-start">
          {displayPlans.map((plan, idx) => {
            const isCurrent = idx === currentIndex;
            const isCancelToBasic = cancelPending && plan.title === 'Basic';
            const isLoadingThis = loadingPlan === plan.priceId;

            const { text, disabled } = (() => {
              if (isCurrent) return { text: 'Current Plan', disabled: true };
              if (isCancelToBasic) return { text: `Back to Basic on ${cancelDateString}`, disabled: true };
              return idx > currentIndex
                ? { text: 'Upgrade', disabled: false }
                : { text: `Back to ${plan.title}`, disabled: false };
            })();

            return (
              <VStack key={plan.title} spacing={8} align="center">
                <Box border="1px solid #FFFFFF1A" borderRadius="20px" p={6} w="400px">
                  <Text fontFamily="Inter" fontWeight="400" fontSize="28px" color="#FFFFFF" mb={3}>
                    {plan.title}
                  </Text>

                  <Text fontFamily="Inter" fontWeight="400" fontSize="14px" color="#FFFFFF80" mb={5}>
                    {plan.description}
                  </Text>

                  <HStack align="baseline" spacing={2} mb={5}>
                    <Text fontFamily="Inter" fontWeight="600" fontSize="75px" color="#FFFFFF" lineHeight="1">
                      {plan.price}
                    </Text>
                    <Text fontFamily="Inter" fontWeight="400" fontSize="14px" color="#FFFFFF">
                      USD/month
                    </Text>
                  </HStack>

                  {isLoadingThis ? (
                    <Box w="300px" h="45px" display="flex" alignItems="center" justifyContent="center">
                      <Spinner thickness="5px" speed="0.65s" emptyColor="gray.700" color="white" size="lg" w="30px" h="30px" />
                    </Box>
                  ) : (
                    <Button
                      display="block" mx="auto" w="300px" h="45px" borderRadius="8px"
                      bg={(isCurrent || isCancelToBasic) ? 'gray.600' : '#298BE2'}
                      fontFamily="Inter" fontWeight="600" color="white" mb={7}
                      isDisabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        plan.title === 'Basic' ? onDialogOpen() : onPayment(plan.priceId);
                      }}
                    >
                      {text}
                    </Button>
                  )}

                  <VStack spacing={3} align="stretch">
                    {plan.features.map((feat, i) => (
                      <HStack key={i} spacing={4} py={4} borderTop={i > 0 ? '1px solid #FFFFFF1A' : undefined}>
                        <Circle size="24px" bg="#298BE2" color="black">
                          <CheckIcon w={3} h={3} />
                        </Circle>
                        <Text fontFamily="Inter" fontWeight="300" fontSize="16px" color="#FFFFFF">
                          {feat}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </VStack>
            );
          })}
        </HStack>

        {showPortalLink && (
          <Box w="100%" textAlign="center" mt={4}>
            {loadingPortal
              ? <Spinner thickness="4px" speed="0.65s" emptyColor="gray.700" color="white" size="md" />
              : <Text fontFamily="Inter" fontWeight="700" fontSize="16px" color="#FFF" cursor="pointer" onClick={onPortal}>
                  Manage Subscription
                </Text>}
          </Box>
        )}

        <AlertDialog isOpen={isDialogOpen} leastDestructiveRef={cancelRef} onClose={onDialogClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">Confirm Cancellation</AlertDialogHeader>
              <AlertDialogBody>
                Are you sure you want to cancel and revert to Basic? You’ll retain access until the end of your billing period.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDialogClose}>No, keep my plan</Button>
                {loadingCancel
                  ? <Spinner thickness="4px" speed="0.65s" emptyColor="gray.700" color="white" size="md" ml={3} />
                  : <Button colorScheme="red" onClick={() => { onCancel(); onDialogClose(); }} ml={3}>Yes, cancel</Button>}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    </Box>
  );
}
